import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./Result.module.css";
import ResultAPI from "../../data/result";
import Subscription from "../Subscription/Subscription";
import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaChartBar,
  FaTrophy,
  FaArrowLeft,
  FaSearch,
  FaTimes,
  FaHistory,
  FaForward,
  FaTable,
  FaList,
  FaCrown,
  FaMedal,
  FaAward,
  FaStar,
  FaChevronUp,
  FaChevronDown,
  FaEquals,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendar,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaHandshake,
  FaFrown,
  FaCloudRain,
  FaArrowRight
} from "react-icons/fa";
import { GlobalContext } from "../../constants/Context";
import { useResultState } from "../../states/useResultState";
import CustomDropdown from "../../components/Input/CustomDropdown";
const Result = () => {
  const { SITE_ID, CLUB_ID } = useContext(GlobalContext);
  const { results, setResults, errors, setErrors, loading, setLoading, filter, setFilter, competitions, setCompetitions, selectedCompetitionId, setSelectedCompetitionId, fromDate, setFromDate, toDate, setToDate, searchTerm, setSearchTerm, groupedResults, setGroupedResults } = useResultState();

  const [season, setSeason] = useState(() => {
    const currentYear = new Date().getFullYear();
    return currentYear.toString();
  });

  const [yearsList, setYearsList] = useState([]);
  const LEAGUE_ID = SITE_ID;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resultsDataLoadedRef = useRef(false);

  const determineResultStatus = useCallback((result) => {
    const resultType = (result || "").toLowerCase();

    if (resultType === "w" || resultType === "won") {
      return "won";
    } else if (resultType === "l" || resultType === "lost") {
      return "lost";
    } else if (resultType === "d" || resultType === "draw") {
      return "draw";
    } else if (resultType === "t" || resultType === "tie") {
      return "tie";
    } else if (resultType === "a" || resultType === "abandoned") {
      return "abandoned";
    } else if (resultType === "c" || resultType === "cancelled") {
      return "cancelled";
    }

    return "unknown";
  }, []);

  const formatMatchDate = (dateString) => {
    if (!dateString || dateString === "Undecided") return "N/A";
    try {
      const [day, month, year] = dateString.split('/');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    } catch (err) {
      return dateString;
    }
  };

  // Get full formatted date for grouping - Same as leagueTable.jsx
  const getFullFormattedDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    try {
      const [day, month, year] = dateString.split('/');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  // Calculate margin of victory
  const getMatchMargin = (result) => {
    if (!result.innings || !result.innings.team1 || !result.innings.team2) return null;

    const firstInningsRuns = parseInt(result.innings.team1.runs) || 0;
    const secondInningsRuns = parseInt(result.innings.team2.runs) || 0;
    const secondInningsWickets = parseInt(result.innings.team2.wickets) || 0;

    if (firstInningsRuns > secondInningsRuns) {
      return `by ${firstInningsRuns - secondInningsRuns} runs`;
    } else if (secondInningsRuns > firstInningsRuns) {
      return `by ${10 - secondInningsWickets} wickets`;
    }

    return null;
  };

  const groupResultsByDate = useCallback((resultsList) => {
    const grouped = {};

    resultsList.forEach(result => {
      const dateStr = getFullFormattedDate(result.match_date);
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(result);
    });

    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        try {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateB - dateA; // Newest first
        } catch (err) {
          return b.localeCompare(a);
        }
      })
      .forEach(date => {
        grouped[date].sort((a, b) => {
          const timeA = a.match_time || "23:59";
          const timeB = b.match_time || "23:59";
          return timeA.localeCompare(timeB);
        });
        sortedGrouped[date] = grouped[date];
      });

    return sortedGrouped;
  }, []);

  // Handle results data from API component
  const handleResultsData = useCallback((data) => {
    if (resultsDataLoadedRef.current) return;
    resultsDataLoadedRef.current = true;

    if (!data || data.length === 0) {
      setErrors("No results found for this league and season");
      setResults([]);
      setGroupedResults({});
      setLoading(false);
      return;
    }

    const leagueResults = data.filter(result =>
      result.league_id && result.league_id.toString() === LEAGUE_ID.toString()
    );

    // Transform results data
    const transformedResults = leagueResults.map(result => {
      // Determine result status
      const resultStatus = determineResultStatus(result.result);

      // Format match date
      const formatMatchDate = (dateString) => {
        if (!dateString) return "Undecided";

        try {
          const [day, month, year] = dateString.split('/');
          return `${day}/${month}/${year}`;
        } catch (e) {
          return dateString;
        }
      };

      // Get team short names
      const getTeamShortName = (clubName, teamName) => {
        if (!clubName && !teamName) return "Undecided";

        const nameToUse = clubName || teamName || "";
        const words = nameToUse.split(' ');
        if (words.length > 1) {
          return words.map(word => word.charAt(0)).join('').toUpperCase();
        }
        return nameToUse.substring(0, 3).toUpperCase();
      };

      // Get full team name
      const getFullTeamName = (clubName, teamName) => {
        if (!clubName) return teamName || "Unknown Team";
        if (!teamName) return clubName;
        return `${clubName} ${teamName}`;
      };

      // Extract innings data
      const getInningsData = (teamId) => {
        const innings = result.innings?.find(inn => inn.team_batting_id === teamId.toString());
        if (!innings) return { runs: "0", wickets: "0", overs: "0.0" };

        return {
          runs: innings.runs || "0",
          wickets: innings.wickets || "0",
          overs: innings.overs || "0.0",
          extras: innings.total_extras || "0"
        };
      };

      // Get winner information
      const getWinnerInfo = () => {
        if (!result.result_description) return null;

        const wonTeamId = result.result_applied_to;
        if (wonTeamId === result.home_team_id) {
          return {
            team: "home",
            description: result.result_description
          };
        } else if (wonTeamId === result.away_team_id) {
          return {
            team: "away",
            description: result.result_description
          };
        }

        return {
          team: "draw/tie",
          description: result.result_description
        };
      };

      // Get points for a team
      const getTeamPoints = (teamId) => {
        const pointData = result.points?.find(p => p.team_id.toString() === teamId.toString());
        return pointData ? pointData.game_points : "0";
      };

      const homeInnings = getInningsData(result.home_team_id);
      const awayInnings = getInningsData(result.away_team_id);
      const winnerInfo = getWinnerInfo();

      // Determine which team batted first
      const battingFirstId = result.batted_first;
      const isHomeBattingFirst = battingFirstId === result.home_team_id;

      return {
        id: result.id,
        match_date: formatMatchDate(result.match_date),
        original_date: result.match_date,
        match_time: result.match_time,
        status: "completed",
        result_status: resultStatus,
        venue: result.ground_name,
        team1_data: {
          id: result.home_team_id,
          name: getFullTeamName(result.home_club_name, result.home_team_name),
          short_name: getTeamShortName(result.home_club_name, result.home_team_name),
          club_name: result.home_club_name,
          team_name: result.home_team_name,
          points: getTeamPoints(result.home_team_id),
          is_batting_first: isHomeBattingFirst
        },
        team2_data: {
          id: result.away_team_id,
          name: getFullTeamName(result.away_club_name, result.away_team_name),
          short_name: getTeamShortName(result.away_club_name, result.away_team_name),
          club_name: result.away_club_name,
          team_name: result.away_team_name,
          points: getTeamPoints(result.away_team_id),
          is_batting_first: !isHomeBattingFirst
        },
        innings: {
          team1: homeInnings,
          team2: awayInnings
        },
        result: result.result,
        result_description: result.result_description,
        winner_info: winnerInfo,
        toss: result.toss || "",
        no_of_overs: result.no_of_overs,
        match_type: result.match_type,
        league: {
          id: result.league_id || LEAGUE_ID,
          name: result.league_name
        },
        competition: {
          id: result.competition_id,
          name: result.competition_name
        },
        umpire_1: result.umpire_1_name,
        umpire_2: result.umpire_2_name,
        original_status: result.status,
        batted_first: battingFirstId
      };
    });

    // Extract unique competitions for filter dropdown
    const uniqueCompetitions = [];
    const compIds = new Set();

    leagueResults.forEach(result => {
      if (result.competition_id && !compIds.has(result.competition_id)) {
        compIds.add(result.competition_id);
        uniqueCompetitions.push({
          id: result.competition_id,
          name: result.competition_name
        });
      }
    });

    setCompetitions(uniqueCompetitions);
    setResults(transformedResults);

    // Group results by date
    const grouped = groupResultsByDate(transformedResults);
    setGroupedResults(grouped);

    setErrors(null);
    setLoading(false);

    // Log result distribution for debugging
    const resultCount = {
      won: transformedResults.filter(r => r.result_status === "won").length,
      lost: transformedResults.filter(r => r.result_status === "lost").length,
      draw: transformedResults.filter(r => r.result_status === "draw").length,
      tie: transformedResults.filter(r => r.result_status === "tie").length,
      abandoned: transformedResults.filter(r => r.result_status === "abandoned").length,
      cancelled: transformedResults.filter(r => r.result_status === "cancelled").length
    };

  }, [determineResultStatus, groupResultsByDate]);

  // Handle API error
  const handleAPIError = useCallback((errorMsg) => {
    console.error("Results API Error:", errorMsg);
    setErrors(`Failed to load results: ${errorMsg}`);
    setLoading(false);
  }, []);

  // Handle API loading
  const handleAPILoading = useCallback((isLoading) => {
    // We handle loading state ourselves
  }, []);

  // Get result status text
  const getResultStatusText = useCallback((result) => {
    switch (result.result_status) {
      case "won":
        return "Won";
      case "lost":
        return "Lost";
      case "draw":
        return "Draw";
      case "tie":
        return "Tie";
      case "abandoned":
        return "Abandoned";
      case "cancelled":
        return "Cancelled";
      default:
        return "Completed";
    }
  }, []);

  const yearOptions = useMemo(() =>
    yearsList?.map(year => ({
      value: year.value,
      label: year.label
    })) || [], [yearsList]
  );

  const competitionOptions = useMemo(() =>
    competitions?.map(comp => ({
      value: comp.id.toString(),
      label: comp.name
    })) || [], [competitions]
  )

  // Get result status class
  const getStatusClass = useCallback((status) => {
    switch (status) {
      case "won":
        return styles.won;
      case "lost":
        return styles.lost;
      case "draw":
      case "tie":
        return styles.draw;
      case "abandoned":
      case "cancelled":
        return styles.cancelled;
      default:
        return styles.completed;
    }
  }, []);

  // Filter and group results by competition, status, and search term
  const filterAndGroupResults = useCallback(() => {
    if (!results.length) return {};

    // Filter by competition
    const filteredByCompetition = selectedCompetitionId === "all"
      ? results
      : results.filter(result =>
        result.competition.id && result.competition.id.toString() === selectedCompetitionId.toString()
      );

    // Further filter by result type and search term
    const filteredByStatusAndSearch = filteredByCompetition.filter((result) => {
      if (filter !== "all" && result.result_status !== filter) {
        return false;
      }

      if (searchTerm.trim() === "") {
        return true;
      }

      const searchLower = searchTerm.toLowerCase();
      return (
        result.team1_data.name.toLowerCase().includes(searchLower) ||
        result.team2_data.name.toLowerCase().includes(searchLower) ||
        result.venue.toLowerCase().includes(searchLower) ||
        result.competition.name.toLowerCase().includes(searchLower)
      );
    });

    // Group the filtered results by date
    return groupResultsByDate(filteredByStatusAndSearch);
  }, [results, selectedCompetitionId, filter, searchTerm, groupResultsByDate]);


  // useEffect for generating years list (same as LeagueList)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2000;
    const endYear = currentYear + 5;

    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push({
        value: year.toString(),
        label: year.toString() + (year === currentYear ? " (Current)" : ""),
      });
    }
    setYearsList(years);

    // Set initial season to current year
    if (!season) {
      setSeason(currentYear.toString());
    }
  }, []);


  // Update grouped results when filters change
  useEffect(() => {
    if (results.length > 0) {
      const filteredGrouped = filterAndGroupResults();
      setGroupedResults(filteredGrouped);
    }
  }, [results, selectedCompetitionId, filter, searchTerm, filterAndGroupResults]);

  // Get result statistics
  const getResultStats = useCallback(() => {
    return {
      total: results.length,
      won: results.filter(r => r.result_status === "won").length,
      lost: results.filter(r => r.result_status === "lost").length,
      draw: results.filter(r => r.result_status === "draw").length,
      tie: results.filter(r => r.result_status === "tie").length,
      abandoned: results.filter(r => r.result_status === "abandoned").length,
      cancelled: results.filter(r => r.result_status === "cancelled").length
    };
  }, [results]);

  const resultStats = getResultStats();

  // Get selected competition name
  const selectedCompetition = selectedCompetitionId === "all"
    ? { name: "All Competitions" }
    : competitions.find(comp => comp.id.toString() === selectedCompetitionId);

  // Reset data loaded ref when season changes
  useEffect(() => {
    resultsDataLoadedRef.current = false;
    setLoading(true);
    setResults([]);
    setGroupedResults({});
    setCompetitions([]);
    setSelectedCompetitionId("all");
    setFromDate("");
    setToDate("");
    setSearchTerm("");
  }, [season]);

  // Handle date filter changes
  const handleDateFilter = useCallback(() => {
    if (fromDate || toDate) {
      resultsDataLoadedRef.current = false;
      setLoading(true);
      setResults([]);
      setGroupedResults({});
      setCompetitions([]);
    }
  }, [fromDate, toDate]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Handle retry
  const handleRetry = () => {
    resultsDataLoadedRef.current = false;
    setLoading(true);
    setResults([]);
    setGroupedResults({});
    setCompetitions([]);
  };

  // Render a single result card
  const renderResultCard = (result, index) => {
    const {
      match_date,
      match_time,
      venue,
      team1_data,
      team2_data,
      innings,
      result_description,
      toss,
      no_of_overs,
      competition,
      match_type,
      umpire_1,
      umpire_2,
      winner_info
    } = result;

    const isHomeWinner = winner_info?.team === "home";
    const isAwayWinner = winner_info?.team === "away";
    const margin = getMatchMargin(result);

    return (
      <div key={result.id || index} className={styles.matchCard}>
        <div className={styles.matchHeader}>
          <div className={styles.matchDateContainer}>
            <FaCalendarAlt />
            <span className={styles.matchDate}>
              {formatMatchDate(match_date)}
            </span>
            {match_time && (
              <span className={styles.matchTime}>
                | {match_time}
              </span>
            )}
          </div>
          <div className={styles.matchFormat}>
            <FaClock />
            <span>{no_of_overs} overs</span>
          </div>
        </div>

        {venue && venue !== "Add New Ground" && venue !== "Undecided" && (
          <div className={styles.venueInfo}>
            <FaMapMarkerAlt />
            <span>{venue}</span>
          </div>
        )}

        <div className={styles.teamsContainer}>
          {/* Home Team */}
          <div className={`${styles.teamRow} ${isHomeWinner ? styles.winningTeam : ''}`}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {team1_data.name}
                </span>
                {isHomeWinner && <span className={styles.winnerBadge}><FaAward /></span>}
              </div>
              <span className={styles.teamRole}>
                {team1_data.is_batting_first ? "Batting First" : "Bowling First"}
              </span>
            </div>
            <div className={styles.scoreInfo}>
              <span className={styles.score}>
                {innings.team1.runs}/{innings.team1.wickets}
              </span>
              <span className={styles.overs}>
                ({innings.team1.overs} ov)
              </span>
            </div>
          </div>

          <div className={styles.vsSeparator}>
            <span>VS</span>
          </div>

          {/* Away Team */}
          <div className={`${styles.teamRow} ${isAwayWinner ? styles.winningTeam : ''}`}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {team2_data.name}
                </span>
                {isAwayWinner && <span className={styles.winnerBadge}><FaAward /></span>}
              </div>
              <span className={styles.teamRole}>
                {team2_data.is_batting_first ? "Batting First" : "Bowling First"}
              </span>
            </div>
            <div className={styles.scoreInfo}>
              <span className={styles.score}>
                {innings.team2.runs}/{innings.team2.wickets}
              </span>
              <span className={styles.overs}>
                ({innings.team2.overs} ov)
              </span>
            </div>
          </div>
        </div>

        <div className={styles.matchFooter}>
          <div className={styles.matchDetails}>
            {margin && (
              <div className={styles.matchMargin}>
                <FaChartBar />
                <span>Won {margin}</span>
              </div>
            )}

            {result_description && (
              <div className={styles.resultInfo}>
                <FaTrophy />
                <span>{result_description}</span>
              </div>
            )}
          </div>

          <div className={styles.matchActions}>
            <Link
              to={`/cricket-match/${result.id}`}
              className={`${styles.actionButton} ${styles.secondary}`}
            >
              <FaChartBar />
              Match Details <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupedResults = () => {
    const dates = Object.keys(groupedResults);

    if (dates.length === 0) {
      return (
        <div className={styles.noResults}>
          <div className={styles.noResultsIcon}>
            {filter === "won" && <FaAward />}
            {filter === "lost" && <FaFrown />}
            {filter === "draw" && <FaHandshake />}
            {filter === "abandoned" && <FaCloudRain />}
            {filter === "all" && <FaChartBar />}
          </div>
          <h3>No {filter !== "all" ? filter : ""} results found</h3>
          <p>Try selecting a different season, date range or filter</p>
          {filter !== "all" && (
            <button
              className={styles.clearFilterButton}
              onClick={() => setFilter("all")}
            >
              Show All Results
            </button>
          )}
          {searchTerm && (
            <button
              className={styles.clearFilterButton}
              onClick={clearSearch}
            >
              Clear Search
            </button>
          )}

        </div>
      );
    }

    return (
      <div className={styles.groupedResults}>
        {dates.map(date => (
          <div key={date} className={styles.dateCategory}>
            <div className={styles.categoryHeader}>
              <div className={styles.categoryTitle}>
                <span className={styles.categoryIcon}>
                  <FaCalendarAlt />
                </span>
                <h4 className={styles.categoryName}>
                  {date}
                </h4>
              </div>
            </div>
            <div className={styles.categoryMatches}>
              {groupedResults[date].map((result, index) => renderResultCard(result, index))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.resultsPage}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading results...</p>
        </div>

        <ResultAPI
          siteId={SITE_ID}
          season={season}
          fromMatchDate={fromDate}
          endMatchDate={toDate}
          divisionId={selectedCompetitionId === "all" ? undefined : selectedCompetitionId}
          onDataLoaded={handleResultsData}
          onError={handleAPIError}
          onLoading={handleAPILoading}
        />
      </div>
    );
  }


  return (
    <div className={styles.resultsPage}>
      <ResultAPI
        siteId={SITE_ID}
        season={season}
        fromMatchDate={fromDate}
        endMatchDate={toDate}
        divisionId={selectedCompetitionId === "all" ? undefined : selectedCompetitionId}
        onDataLoaded={handleResultsData}
        onError={handleAPIError}
        onLoading={handleAPILoading}
      />

      {results.length >= 0 && (
        <section className={styles.heroSection}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>Cricket Results</div>
              <h1 className={styles.heroTitle}>
                <span style={{ color: "white" }}>Royal Sports League</span>
              </h1>
              <p className={styles.heroSubtitle} style={{ color: "white" }}>
                Match results from {season} season. View scores, innings details and match outcomes.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className={styles.seasonSelectorSection}>
        <div className={styles.container}>
          <div className={styles.selectorCard}>
            <h3>Filter Results</h3>
            <div className={styles.selectorControls}>
              <div className={styles.dateFilters}>

                <div className={styles.selectGroup}>
                  <label htmlFor="seasonSelect">Select Season:</label>
                  <div className={styles.selectWrapper}>
                    <CustomDropdown
                      options={yearOptions}
                      value={season}
                      onChange={(newSeason) => {
                        setSeason(newSeason);
                        resultsDataLoadedRef.current = false;
                        setLoading(true);
                        setResults([]);
                        setGroupedResults({});
                        setCompetitions([]);
                        setSelectedCompetitionId("all");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                    />
                  </div>
                </div>

                {competitions.length > 0 && (
                  <div className={styles.selectGroup}>
                    <label htmlFor="competitionSelect">Filter by Competition:</label>
                    <CustomDropdown
                      options={competitionOptions}
                      value={selectedCompetitionId}
                      onChange={(value) => setSelectedCompetitionId(value)}
                    />
                  </div>
                )}
              </div>

              <div className={styles.dateFilters}>
                <div className={styles.dateInputGroup}>
                  <label htmlFor="fromDate">From Date:</label>
                  <input
                    type="date"
                    id="fromDate"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      handleDateFilter();
                    }}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label htmlFor="toDate">To Date:</label>
                  <input
                    type="date"
                    id="toDate"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      handleDateFilter();
                    }}
                    className={styles.dateInput}
                  />
                </div>
                {(fromDate || toDate) && (
                  <button
                    className={styles.clearDateButton}
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                      resultsDataLoadedRef.current = false;
                      setLoading(true);
                    }}
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="results" className={styles.resultsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {selectedCompetition?.name || "All Results"} - {season} Season
            </h2>

            {/* Search Box */}
            <div className={styles.searchWrapper}>
              <div className={styles.searchBox}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search teams, venues or competitions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                  aria-label="Search results"
                />
                {searchTerm && (
                  <button
                    className={styles.searchClear}
                    onClick={clearSearch}
                    aria-label="Clear search"
                    type="button"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>
          {renderGroupedResults()}
        </div>
      </section>
      <Subscription />
    </div>
  );
};

export default Result;